const TAG_LABELS = {
  keynote: "Keynote",
  panel: "Panel",
  fireside: "Fireside",
  practical: "Practical AI",
  strategy: "Strategy",
  networking: "Break",
  main: "Main stage",
};

function programTag(item) {
  if (item.type) return TAG_LABELS[item.type.toLowerCase()] || item.type;
  const title = (item.title || "").toLowerCase();
  if (title.includes("keynote")) return "Keynote";
  if (title.includes("panel")) return "Panel";
  if (title.includes("fireside")) return "Fireside";
  return TAG_LABELS[item.track] || "Main stage";
}

function scrollToId(id) {
  return (e) => {
    e.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
}

function ProgramRow({ item, idx }) {
  const tag = programTag(item);
  const isKeynote = tag === "Keynote";
  const isBreak = tag === "Break";
  return (
    <li
      data-testid={`program-item-${idx}`}
      className={`program-row grid grid-cols-12 gap-x-4 md:gap-x-10 items-baseline ${
        isBreak ? "program-row--break" : ""
      }`}
    >
      <span className="program-time col-span-3 md:col-span-2">{item.time}</span>
      <div className="col-span-9 md:col-span-7 flex flex-col gap-1.5">
        <p
          className={`program-title ${isBreak ? "program-title--break" : ""}`}
          data-testid={`program-title-${idx}`}
        >
          {item.title}
        </p>
        {item.description && (
          <p className="text-[13px] md:text-[14px] text-[var(--fg-muted)] max-w-[58ch] leading-[1.55]">
            {item.description}
          </p>
        )}
      </div>
      <div className="hidden md:flex col-span-3 justify-end">
        <span
          className={`tag ${isKeynote ? "tag--accent" : ""}`}
          data-testid={`program-tag-${idx}`}
        >
          {tag}
        </span>
      </div>
    </li>
  );
}

export default function Program({ program }) {
  const items = program && program.length > 0 ? program : [];
  const morning = items.filter((p) => (p.time || "") < "12:15");
  const afternoon = items.filter((p) => (p.time || "") >= "12:15");

  const segments = [
    { label: "Morning", range: "08:30 — 12:15", rows: morning },
    { label: "Afternoon", range: "12:15 — 18:00", rows: afternoon },
  ].filter((s) => s.rows.length > 0);

  return (
    <section id="program" data-testid="program-section" className="section border-t border-[var(--rule)]">
      <div className="max-w-page container-x">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-12 md:mb-16">
          <div className="md:col-span-8">
            <div className="flex items-center gap-3 mb-6">
              <span className="section-index">04</span>
              <span className="eyebrow" data-testid="program-eyebrow">Program · Preliminary</span>
            </div>
            <h2 className="font-display text-4xl sm:text-5xl md:text-6xl leading-[0.98] tracking-[-0.02em] max-w-[16ch]">
              One day. Two tracks. One room.
            </h2>
          </div>
          <div className="md:col-span-4 flex md:items-end md:justify-end">
            <p
              className="text-[14px] md:text-[15px] text-[var(--fg-muted)] max-w-[38ch] leading-[1.55]"
              data-testid="program-note"
            >
              A working skeleton — times and titles shift as speakers confirm.
              Final program published closer to the day.
            </p>
          </div>
        </div>

        {items.length === 0 && (
          <p className="py-12 text-[14px] text-[var(--fg-muted)] border-t border-[var(--rule)]" data-testid="program-empty">
            Program is being finalised.
          </p>
        )}

        {segments.map((seg) => (
          <div key={seg.label} data-testid={`program-segment-${seg.label.toLowerCase()}`}>
            <div className="flex items-baseline justify-between border-t border-[var(--fg)] pt-4 pb-2 mt-10 first:mt-0">
              <span className="text-[11px] font-semibold uppercase tracking-[0.22em]">
                {seg.label}
              </span>
              <span className="text-[11px] uppercase tracking-[0.18em] text-[var(--fg-muted)] tabular-nums">
                {seg.range}
              </span>
            </div>
            <ul className="flex flex-col">
              {seg.rows.map((p) => (
                <ProgramRow key={p.id} item={p} idx={items.indexOf(p)} />
              ))}
            </ul>
          </div>
        ))}

        {items.length > 0 && (
          <div className="border-t border-[var(--rule)] mt-10 pt-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <p className="text-[12px] tracking-[0.16em] uppercase text-[var(--fg-muted)]">
              All sessions in English · Full program announced with the date
            </p>
            <a
              href="#early-access"
              onClick={scrollToId("early-access")}
              className="text-link w-fit"
              data-testid="program-cta"
            >
              Get the program first <span aria-hidden>→</span>
            </a>
          </div>
        )}
      </div>
    </section>
  );
}
